import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../utils/api";

const ImageDatabaseView = () => {
  const { business_id } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchImages();
  }, [business_id]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/field_post/by-business/${business_id}`);
      
      if (response.data.success) {
        setImages(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      setError("Error loading images");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 font-medium">Loading images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                📸 Image Gallery
              </h1>
              <p className="text-gray-600">
                Total images: {images.length}
              </p>
            </div>
            <Link
              to={`/image_data_base/${business_id}`}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg"
            >
              + Upload New Image
            </Link>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Image Grid */}
        {images.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No images yet
            </h3>
            <p className="text-gray-500 mb-6">
              Start by uploading your first image
            </p>
            <Link
              to={`/image_data_base/${business_id}`}
              className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
            >
              Upload First Image
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image) => (
              <div
                key={image._id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105 cursor-pointer"
                onClick={() => setSelectedImage(image)}
              >
                <div className="relative h-64 bg-gray-200">
                  <img
                    src={image.image_url}
                    alt="Field"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">
                      {formatDate(image.createdAt)}
                    </span>
                    {image.coordinates && image.coordinates.latitude && image.coordinates.longitude ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openGoogleMaps(
                            image.coordinates.latitude,
                            image.coordinates.longitude
                          );
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        📍 View on map
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">No location</span>
                    )}
                  </div>
                  {image.employee_name && (
                    <p className="text-gray-700 font-medium mb-1">
                      👤 {image.employee_name}
                    </p>
                  )}
                  {image.notes && (
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {image.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expanded Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-gray-800">
                  Image Details
                </h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <img
                src={selectedImage.image_url}
                alt="Field"
                className="w-full rounded-lg mb-4"
              />
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-700">📅 Date:</span>
                  <span className="text-gray-600">
                    {formatDate(selectedImage.createdAt)}
                  </span>
                </div>
                
                {selectedImage.employee_name && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">👤 Employee:</span>
                    <span className="text-gray-600">
                      {selectedImage.employee_name}
                    </span>
                  </div>
                )}
                
                {selectedImage.coordinates && selectedImage.coordinates.latitude && selectedImage.coordinates.longitude ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">📍 Location:</span>
                    <button
                      onClick={() =>
                        openGoogleMaps(
                          selectedImage.coordinates.latitude,
                          selectedImage.coordinates.longitude
                        )
                      }
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View on Google Maps
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">📍 Location:</span>
                    <span className="text-gray-400">Not available</span>
                  </div>
                )}
                
                {selectedImage.notes && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700 block mb-2">
                      📝 Notes:
                    </span>
                    <p className="text-gray-600">{selectedImage.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageDatabaseView;

